<template>

  <v-form @submit.prevent="submit" novalidate>

    <v-row>

      <v-col v-if="canUseOrgFilter" cols="12" sm="6" :md="canUseOrgFilter ? 3 : 4" lg="3" xl="2">
        <o-autocomplete
          :label="$t('titles.organization')"
          v-model="filters.organizationId"
          :items="Organization.getOptions()"
        />
      </v-col>

      <v-col cols="12" sm="6" :md="canUseOrgFilter ? 3 : 4" lg="3" xl="2">
        <o-text-field
          :label="$t('titles.location')"
          v-model="filters.locationName"
          clearable
        />
      </v-col>

      <v-col cols="12" sm="6" :md="canUseOrgFilter ? 3 : 4" lg="3" :xl="canUseOrgFilter ? 2 : 2">
        <o-select
          :label="$t('titles.status')"
          v-model="filters.status"
          :items="[
            {value: 'all', title: 'All'},
            {value: 'onboarded', title: $t('enums.location_statuses.onboarded')},
            {value: 'live', title: $t('enums.location_statuses.live')},
            {value: 'onboarded_live', title: 'Onboarded & Live'},
            {value: 'archived', title: $t('enums.location_statuses.archived')}
          ]"
        />
      </v-col>

      <v-col cols="12" sm="6" :md="canUseOrgFilter ? 3 : 4" :lg="canUseOrgFilter ? 3 : 6" :xl="canUseOrgFilter ? 6 : 8" class="text-end">
        Showing {{ filteredItems.length }} matching locations
      </v-col>

    </v-row>

    <v-data-table
      ref="dataTable"
      :headers="headers"
      :items="filteredItems"
      :loading="loadingSolutions"
      :sort-by="[{ key: 'locationName' }]"
      v-model:page="page"
      :items-per-page="itemsPerPage"
      class="mb-12"
    >

      <!-- header templates -->

      <template
        v-for="solution in visibleSolutions"
        v-slot:[`header.solution_${solution}`]="{ column }"
      >

        {{ column.title }}<br>

        <v-btn-toggle
          :model-value="solutionToggleValues[solution]"
          divided
          variant="outlined"
          density="comfortable"
          class="mb-4"
        >
          <v-btn :disabled="!editableSolutions.includes(solution)" value="none" @click.stop="setSolutionForAllLocations('none', solution)" icon="$userSolutionsNone" />
          <v-btn v-if="!solutionsWithoutRead.includes(solution)" :disabled="!editableSolutions.includes(solution)" value="read" @click.stop="setSolutionForAllLocations('read', solution)" icon="$view" />
          <v-btn :disabled="!editableSolutions.includes(solution)" value="write" @click.stop="setSolutionForAllLocations('write', solution)" icon="$edit" />
        </v-btn-toggle>

      </template>

      <!-- item templates -->

      <template v-slot:item.locationName="{ item }">
        <router-link :to="{ name: 'locationDetail', params: { 'locationID': item.locationID } }">
          {{ item.locationName }}
        </router-link>
      </template>

      <template v-slot:item.all="{ item }">

        <v-btn-toggle
          :model-value="getLocationToggleValue(item)"
          divided
          variant="outlined"
          density="comfortable"
        >
          <v-btn :disabled="!editableSolutions.length" value="none" @click="setAllSolutions('none', item)" icon="$userSolutionsNone" />
          <v-btn :disabled="!editableSolutions.length" value="read" @click="setAllSolutions('read', item)" icon="$view" />
          <v-btn :disabled="!editableSolutions.length" value="write" @click="setAllSolutions('write', item)" icon="$edit" />
        </v-btn-toggle>

      </template>

      <template
        v-for="solution in visibleSolutions"
        v-slot:[`item.solution_${solution}`]="{ item }"
      >
        <o-select
          v-model="item['solution_' + solution]"
          :items="solutionsWithoutRead.includes(solution) ? permissionOptionsWithoutRead : permissionOptions"
          density="compact"
          hide-details
          :disabled="!editableSolutions.includes(solution)"
        />
      </template>

      <template v-slot:bottom>
        <!-- This is just here to hide the pagination controls. -->
      </template>

    </v-data-table>

    <v-btn
      type="submit"
      prepend-icon="$confirm"
      :loading="submitting"
      :disabled="submitting"
    >Save</v-btn>

  </v-form>

</template>

<script setup>

import { computed, inject, onBeforeMount, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'

// models
import Location from '../../../models/Location'
import Organization from '../../../models/Organization'
import User from '../../../models/User'
import UserSolution from '../../../models/UserSolution'

// enums
import { Solutions } from '../../../enums'

// misc
import NotificationService from '../../../services/NotificationService'

const props = defineProps({

  user: {
    type: User,
    required: true,
  },

})

const $can = inject('$can')
const { t } = useI18n()
const title = useTitle()
title.value = t('titles.user_solutions_with_username', {username: props.user.username})

const permissionOptions = [
  {value: 'none', title: t('enums.read_write_permissions.none')},
  {value: 'read', title: t('enums.read_write_permissions.read')},
  {value: 'write', title: t('enums.read_write_permissions.write')},
]

const permissionOptionsWithoutRead = [
  {value: 'none', title: t('enums.read_write_permissions.none')},
  {value: 'write', title: t('enums.read_write_permissions.write')},
]

import { auth } from '../../../store/auth.module'
import OSelect from '../../forms/OSelect.vue'
import OAutocomplete from '../../forms/OAutocomplete.vue'

const isSuperAdmin = auth.state.user.role === 'super_admin'
const canUseOrgFilter = isSuperAdmin && props.user.role === 'super_admin'
const visibleSolutions = (
  isSuperAdmin ?
  ['universalInventory', 'rateManagement', 'blackouts', 'reporting', 'uri', 'terminal'] :
  ['universalInventory', 'rateManagement', 'blackouts', 'reporting']
)
const editableSolutions = (
  props.user.role === 'super_admin' ?
  ['terminal'] :
  ['universalInventory', 'rateManagement', 'blackouts', 'reporting']
)
const solutionsWithoutRead = ['uri', 'terminal']

/**
 * Filters
 */

const filters = reactive({
  locationName: '',
  status: 'onboarded_live',
  organizationId: props.user.organizationID,
})

/**
 * Data table configs
 */

const headers = [
  { title: t('titles.location'), key: 'locationName' },
  { title: t('titles.all'), key: 'all', sortable: false },
]

for (const solution of visibleSolutions) {
  headers.push({
    key: 'solution_' + solution,
    title: t(`enums.solutions.${solution}`),
    sortable: false,
  })
}

const page = ref(1)
const itemsPerPage = ref(-1) // show all

/**
 * Load user solutions
 */

const items = ref([])

const filteredItems = computed(() => {

  const nameFilter = filters.locationName.trim().toUpperCase()

  return items.value.filter(item => {
    return nameFilter === 0 || item.locationName.toUpperCase().indexOf(nameFilter) !== -1
  }).filter(item => {
    switch (filters.status) {
      case 'all':
        return true
      case 'onboarded_live':
        return ['onboarded', 'live'].includes(item.status)
      default:
        return item.status === filters.status
    }
  }).filter(item => {
    if (!canUseOrgFilter) return true
    return item.organizationID === filters.organizationId
  })

})

const loadingSolutions = ref(false)

onBeforeMount(() => {

  loadingSolutions.value = true

  UserSolution.fetchByUserID(props.user.ID)
    .then(() => {
      populateItems()
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error(t('alerts.couldnt_load_user_solutions'))
    })
    .finally(() => {
      loadingSolutions.value = false
    })

})

function populateItems() {

  items.value = []

  const locationQuery = Location.query()
    .with('userSolutions', (query) => {
      query.where('userID', props.user.ID)
    })

  if (props.user.role !== 'super_admin') {
    locationQuery.where('organizationID', props.user.organizationID)
  }

  const locations = locationQuery.all()

  items.value = locations.map((location) => {

    const item = {
      locationID: location.ID,
      locationName: location.locationName,
      organizationID: location.organizationID,
      status: location.status,
      original: {},
    }

    const existingUserSolutions = location.userSolutions.length ? location.userSolutions[0] : null

    visibleSolutions.forEach((solution) => {
      let permission = 'none'
      if (existingUserSolutions) {
        const writeKey = solution + 'Write'
        const readKey = solution + 'Read'
        if (existingUserSolutions[writeKey]) {
          permission = 'write'
        } else if (existingUserSolutions[readKey]) {
          permission = 'read'
        }
      }
      item['solution_' + solution] = permission
      item.original[solution] = permission
    })

    return item

  })

}

/**
 * Location toggles (in data rows)
 */

function getLocationToggleValue(item) {

  let readableSolutions = 0
  let writableSolutions = 0

  visibleSolutions.forEach((solution) => {
    const key = 'solution_' + solution
    switch (item[key]) {
      case 'read':
        readableSolutions++
        break
      case 'write':
        writableSolutions++
        break
    }
  })

  if (readableSolutions === 0 && writableSolutions === 0) {
    return 'none'
  }

  if (readableSolutions === visibleSolutions.length && writableSolutions === 0) {
    return 'read'
  }

  if (readableSolutions === 0 && writableSolutions === visibleSolutions.length) {
    return 'write'
  }

  return null

}

function setAllSolutions(permission, item) {

  visibleSolutions.forEach((solution) => {
    if (!editableSolutions.includes(solution)) return
    const key = 'solution_' + solution
    if (permission === 'read' && solutionsWithoutRead.includes(solution)) permission = 'none'
    item[key] = permission
  })

}

/**
 * Solution toggles (in header cells)
 */

const solutionToggleValues = computed(() => {

  const output = {}

  const numLocations = items.value.length

  visibleSolutions.forEach((solution) => {
    
    const key = 'solution_' + solution
    let readableLocations = 0
    let writableLocations = 0

    items.value.forEach((item) => {
      switch (item[key]) {
        case 'read':
          readableLocations++
          break
        case 'write':
          writableLocations++
          break
      }
    })

    let permission = null

    if (readableLocations === 0 && writableLocations === 0) {
      permission = 'none'
    } else if (readableLocations === numLocations && writableLocations === 0) {
      permission = 'read'
    } else if (readableLocations === 0 && writableLocations === numLocations) {
      permission = 'write'
    }

    output[solution] = permission

  })

  return output

})

function setSolutionForAllLocations(permission, solution) {

  const key = 'solution_' + solution

  filteredItems.value.forEach((item) => {
    item[key] = permission
  })

}

/**
 * Submission
 */

const submitting = ref(false)

function submit() {

  submitting.value = true
  const solutions = Object.keys(Solutions)
  const promises = []

  items.value.forEach((item) => {

    let itemHasChanged = false

    const payload = {
      locationID: item.locationID,
      // These solutions are not handled by the FE, but the BE expects them to be in the payload
      rateParityRead: false,
      rateParityWrite: false,
      maxCapacityRead: false,
      maxCapacityWrite: false,
    }

    solutions.forEach((solution) => {
      const key = 'solution_' + solution
      payload[solution + 'Read'] = item[key] === 'read' || item[key] === 'write'
      payload[solution + 'Write'] = item[key] === 'write'
      if (item[key] !== item.original[solution]) {
        itemHasChanged = true
      }
    })

    if (itemHasChanged) {
      promises.push(UserSolution.updateForUserID(props.user.ID, payload))
    }

  })

  Promise.all(promises)
    .then(() => {
      populateItems() // update each item's .original object
      NotificationService.success(t('alerts.user_solutions_updated'))
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error(t('alerts.couldnt_update_user_solutions'))
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
